import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Custom base query for admin endpoints - uses adminToken instead of user token
const adminBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BASE_URL,
  prepareHeaders: (headers) => {
    const adminToken = localStorage.getItem("adminToken");
    if (adminToken) {
      headers.set("Authorization", `Bearer ${adminToken}`);
    }
    return headers;
  },
  timeout: 5 * 60 * 1000,
});

export const adminApiSlice = createApi({
  reducerPath: "adminApi",
  tagTypes: ["AdminUser", "AdminProject", "AdminCommunity", "AdminResult", "AdminFund", "Analytics"],
  baseQuery: adminBaseQuery,

  endpoints: (builder) => ({
    // Analytics
    getAnalytics: builder.query({
      query: () => "/admin/analytics",
      providesTags: ["Analytics"],
    }),

    // Users
    getAdminUsers: builder.query({
      query: ({ page = 1, limit = 20, search, role, auth_provider, deleted, sortBy, sortOrder }) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (search) params.append("search", search);
        if (role) params.append("role", role);
        if (auth_provider) params.append("auth_provider", auth_provider);
        if (deleted) params.append("deleted", deleted);
        if (sortBy) params.append("sortBy", sortBy);
        if (sortOrder) params.append("sortOrder", sortOrder);

        return `/admin/users?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.users.map(({ _id }) => ({ type: "AdminUser", id: _id })),
              { type: "AdminUser", id: "LIST" },
            ]
          : [{ type: "AdminUser", id: "LIST" }],
    }),

    getAdminUserById: builder.query({
      query: (id) => `/admin/users/${id}`,
      providesTags: (result, error, id) => [{ type: "AdminUser", id }],
    }),

    deleteAdminUser: builder.mutation({
      query: ({ id, permanent }) => ({
        url: `/admin/users/${id}${permanent ? "?permanent=true" : ""}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "AdminUser", id },
        { type: "AdminUser", id: "LIST" },
        "Analytics",
      ],
    }),

    restoreAdminUser: builder.mutation({
      query: (id) => ({
        url: `/admin/users/${id}/restore`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "AdminUser", id },
        { type: "AdminUser", id: "LIST" },
        "Analytics",
      ],
    }),

    // Sync user wallet activities (link historical records)
    syncUserWalletActivities: builder.mutation({
      query: (id) => ({
        url: `/admin/users/${id}/sync-wallet-activities`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "AdminUser", id },
      ],
    }),

    // Sync all users' wallet activities
    syncAllUsersWalletActivities: builder.mutation({
      query: () => ({
        url: `/admin/users/sync-all-wallet-activities`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "AdminUser", id: "LIST" },
        "Analytics",
      ],
    }),

    // Projects
    getAdminProjects: builder.query({
      query: ({ page = 1, limit = 20, search, state, category, visible, sortBy, sortOrder }) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (search) params.append("search", search);
        if (state) params.append("state", state);
        if (category) params.append("category", category);
        // Only append visible filter if it's explicitly "true" or "false", not empty string
        if (visible === "true" || visible === "false") params.append("visible", visible);
        if (sortBy) params.append("sortBy", sortBy);
        if (sortOrder) params.append("sortOrder", sortOrder);

        return `/admin/projects?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.projects.map(({ _id }) => ({ type: "AdminProject", id: _id })),
              { type: "AdminProject", id: "LIST" },
            ]
          : [{ type: "AdminProject", id: "LIST" }],
    }),

    getAdminProjectById: builder.query({
      query: (id) => `/admin/projects/${id}`,
      providesTags: (result, error, id) => [{ type: "AdminProject", id }],
    }),

    deleteAdminProject: builder.mutation({
      query: (id) => ({
        url: `/admin/projects/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "AdminProject", id },
        { type: "AdminProject", id: "LIST" },
        "Analytics",
      ],
    }),

    updateProjectVisibility: builder.mutation({
      query: ({ id, visible }) => ({
        url: `/admin/projects/${id}/visibility`,
        method: "PUT",
        body: { visible },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "AdminProject", id },
        { type: "AdminProject", id: "LIST" },
      ],
    }),

    // Communities
    getAdminCommunities: builder.query({
      query: ({ page = 1, limit = 20, search, sortBy, sortOrder }) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (search) params.append("search", search);
        if (sortBy) params.append("sortBy", sortBy);
        if (sortOrder) params.append("sortOrder", sortOrder);

        return `/admin/communities?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.communities.map(({ _id }) => ({ type: "AdminCommunity", id: _id })),
              { type: "AdminCommunity", id: "LIST" },
            ]
          : [{ type: "AdminCommunity", id: "LIST" }],
    }),

    deleteAdminCommunity: builder.mutation({
      query: (id) => ({
        url: `/admin/communities/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "AdminCommunity", id },
        { type: "AdminCommunity", id: "LIST" },
        "Analytics",
      ],
    }),

    // Sync community members from blockchain
    syncCommunityMembers: builder.mutation({
      query: (id) => ({
        url: `/admin/communities/${id}/sync-members`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "AdminCommunity", id },
        { type: "AdminCommunity", id: "LIST" },
      ],
    }),

    // Results
    getAdminResults: builder.query({
      query: ({ page = 1, limit = 20, search, sortBy, sortOrder }) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (search) params.append("search", search);
        if (sortBy) params.append("sortBy", sortBy);
        if (sortOrder) params.append("sortOrder", sortOrder);

        return `/admin/results?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ _id }) => ({ type: "AdminResult", id: _id })),
              { type: "AdminResult", id: "LIST" },
            ]
          : [{ type: "AdminResult", id: "LIST" }],
    }),

    deleteAdminResult: builder.mutation({
      query: (id) => ({
        url: `/admin/results/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "AdminResult", id },
        { type: "AdminResult", id: "LIST" },
        "Analytics",
      ],
    }),

    // Funds
    getAdminFunds: builder.query({
      query: ({ page = 1, limit = 20, sortBy, sortOrder }) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (sortBy) params.append("sortBy", sortBy);
        if (sortOrder) params.append("sortOrder", sortOrder);

        return `/admin/funds?${params.toString()}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.funds.map(({ _id }) => ({ type: "AdminFund", id: _id })),
              { type: "AdminFund", id: "LIST" },
            ]
          : [{ type: "AdminFund", id: "LIST" }],
    }),

    // Verify session
    verifyAdminSession: builder.query({
      query: () => "/admin/verify",
    }),
  }),
});

export const {
  useGetAnalyticsQuery,
  useGetAdminUsersQuery,
  useGetAdminUserByIdQuery,
  useDeleteAdminUserMutation,
  useRestoreAdminUserMutation,
  useSyncUserWalletActivitiesMutation,
  useSyncAllUsersWalletActivitiesMutation,
  useGetAdminProjectsQuery,
  useGetAdminProjectByIdQuery,
  useDeleteAdminProjectMutation,
  useUpdateProjectVisibilityMutation,
  useGetAdminCommunitiesQuery,
  useDeleteAdminCommunityMutation,
  useSyncCommunityMembersMutation,
  useGetAdminResultsQuery,
  useDeleteAdminResultMutation,
  useGetAdminFundsQuery,
  useVerifyAdminSessionQuery,
} = adminApiSlice;
